import React, { useEffect, useMemo, useState } from "react";
import jsonData from "../../Helper/CountryStateCity.json";
import Select from "react-select";
import { useSelector } from "react-redux";
import makeAnimated from "react-select/animated";
import "./Location.css";

const animatedComponents = makeAnimated();

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 42,
    borderRadius: 10,
    border: state.isFocused ? "1.5px solid #4893B3" : "1.5px solid #dce8f2",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(72, 147, 179, 0.12)" : "none",
    background: "#fff",
    "&:hover": {
      borderColor: state.isFocused ? "#4893B3" : "#b7c9d6",
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    background: "#e8f4f8",
    borderRadius: 6,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#0f7898",
    fontWeight: 600,
    fontSize: "0.8125rem",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#0f7898",
    ":hover": {
      background: "#4893B3",
      color: "#fff",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9aadb9",
    fontSize: "0.875rem",
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected
      ? "#4893B3"
      : state.isFocused
        ? "#e8f4f8"
        : "#fff",
    color: state.isSelected ? "#fff" : "#102433",
    fontWeight: state.isSelected ? 600 : 500,
    fontSize: "0.875rem",
  }),
};

/**
 * Location picker
 * @param {function} sendDataToParent
 * @param {boolean} multiState - allow multiple states (job posting)
 * @param {boolean} multiCity - allow multiple cities (default true)
 * @param {boolean} prefillFromJob - hydrate from Redux jobdetails (edit job)
 */
const Location = ({
  sendDataToParent,
  multiState = false,
  multiCity = true,
  prefillFromJob = false,
}) => {
  const { jobdetails } = useSelector((state) => state?.Jobs);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const countries = useMemo(
    () =>
      jsonData.map((country) => ({
        value: country.name,
        label: country.name,
      })),
    []
  );

  const states = useMemo(() => {
    const countryData = jsonData.find((c) => c.name === selectedCountry);
    return (countryData?.states || []).map((state) => ({
      value: state.name,
      label: state.name,
    }));
  }, [selectedCountry]);

  const cities = useMemo(() => {
    const countryData = jsonData.find((c) => c.name === selectedCountry);
    if (!countryData || !selectedStates.length) return [];

    const cityMap = new Map();
    countryData.states
      .filter((state) => selectedStates.includes(state.name))
      .forEach((state) => {
        (state.cities || []).forEach((city) => {
          if (!cityMap.has(city.name)) {
            cityMap.set(city.name, {
              value: city.name,
              label: city.name,
            });
          }
        });
      });

    return Array.from(cityMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [selectedCountry, selectedStates]);

  const cityOptionValues = useMemo(
    () => new Set(cities.map((c) => c.value)),
    [cities]
  );

  // Keep cities only if they still belong to selected states
  useEffect(() => {
    setSelectedCities((prev) =>
      prev.filter((city) => cityOptionValues.has(city))
    );
  }, [cityOptionValues]);

  // Notify parent
  useEffect(() => {
    if (typeof sendDataToParent !== "function") return;
    sendDataToParent({
      Country: selectedCountry,
      State: multiState ? selectedStates : selectedStates[0] || "",
      States: selectedStates,
      Cities: selectedCities,
    });
    // Parent often passes an inline callback; depend on location values only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, selectedStates, selectedCities, multiState]);

  // Prefill from job edit
  useEffect(() => {
    if (!prefillFromJob || !jobdetails || hydrated) return;

    const country = jobdetails.country || "";
    const stateList = toArray(jobdetails.state);
    const cityList = toArray(jobdetails.cities);

    setSelectedCountry(country);
    setSelectedStates(stateList);
    setSelectedCities(cityList);
    setHydrated(true);
  }, [jobdetails, hydrated, prefillFromJob]);

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption?.value || "");
    setSelectedStates([]);
    setSelectedCities([]);
  };

  const handleStateChange = (selectedOption) => {
    if (multiState) {
      const values = (selectedOption || []).map((opt) => opt.value);
      setSelectedStates(values);
    } else {
      const value = selectedOption?.value || "";
      setSelectedStates(value ? [value] : []);
      setSelectedCities([]);
    }
  };

  const handleCityChange = (selectedOption) => {
    if (multiCity) {
      const values = (selectedOption || []).map((opt) => opt.value);
      setSelectedCities(values);
    } else {
      const value = selectedOption?.value || "";
      setSelectedCities(value ? [value] : []);
    }
  };

  return (
    <div className="jsp-location">
      <div className="jsp-location-field">
        <label htmlFor="countrySelect" className="jsp-location-label">
          Country
        </label>
        <Select
          id="countrySelect"
          styles={selectStyles}
          placeholder="Select country"
          value={countries.find((option) => option.value === selectedCountry) || null}
          onChange={handleCountryChange}
          options={countries}
          isClearable
        />
      </div>

      <div className="jsp-location-field">
        <label htmlFor="stateSelect" className="jsp-location-label">
          State{multiState ? "s" : ""}
          {multiState && <span className="jsp-location-hint"> (multi-select)</span>}
        </label>
        <Select
          id="stateSelect"
          styles={selectStyles}
          placeholder={multiState ? "Select one or more states" : "Select state"}
          value={
            multiState
              ? states.filter((option) => selectedStates.includes(option.value))
              : states.find((option) => option.value === selectedStates[0]) || null
          }
          onChange={handleStateChange}
          options={states}
          isMulti={multiState}
          isDisabled={!selectedCountry}
          isClearable
          closeMenuOnSelect={!multiState}
          components={multiState ? animatedComponents : undefined}
        />
      </div>

      <div className="jsp-location-field">
        <label htmlFor="citySelect" className="jsp-location-label">
          Cit{multiCity ? "ies" : "y"}
          {multiCity && <span className="jsp-location-hint"> (multi-select)</span>}
        </label>
        <Select
          id="citySelect"
          styles={selectStyles}
          classNamePrefix="react-select"
          placeholder={
            !selectedStates.length
              ? "Select state first"
              : multiCity
                ? "Select one or more cities"
                : "Select city"
          }
          value={
            multiCity
              ? cities.filter((option) => selectedCities.includes(option.value))
              : cities.find((option) => option.value === selectedCities[0]) || null
          }
          onChange={handleCityChange}
          options={cities}
          isMulti={multiCity}
          isDisabled={!selectedStates.length}
          isClearable
          closeMenuOnSelect={!multiCity}
          components={multiCity ? animatedComponents : undefined}
        />
      </div>
    </div>
  );
};

export default Location;
